import { DEFAULT_FETCHING_STATE, DEFAULT_NO_ERROR } from "state/constants";
import Types from "./types";

const INITIAL_STATE = {
    dashboard: {
        tokenBalance: 0,
        totalEmployees: 0,
        activeEmployees: 0,
        sessionsAssigned: 0,
        sessionsUsed: 0,
        sessionsAvailable: 0,
        usageByMonth: [],
        businessName: '',
        planType: '',
        recentTransactions: [],
    },
    wellnessSummary: {
        nom035: {
            riskDistribution: { nulo: 0, bajo: 0, medio: 0, alto: 0, muyAlto: 0 },
            avgScore: 0,
            requiresFollowUpCount: 0,
            participationRate: 0,
            lastAssessmentDate: null,
        },
        sessionRisk: {
            riskDistribution: { none: 0, low: 0, medium: 0, high: 0, critical: 0 },
            highRiskPercentage: 0,
            totalSessions: 0,
        },
        trend: [],
    },
    employees: {
        data: [],
        total: 0,
        page: 1,
        limit: 10,
    },
    tokenHistory: {
        data: [],
        total: 0,
    },
    fetching: {
        dashboard: { ...DEFAULT_FETCHING_STATE },
        wellnessSummary: { ...DEFAULT_FETCHING_STATE },
        employees: { ...DEFAULT_FETCHING_STATE },
        addEmployee: { ...DEFAULT_FETCHING_STATE },
        deleteEmployee: { ...DEFAULT_FETCHING_STATE },
        assignSessions: { ...DEFAULT_FETCHING_STATE },
        tokenHistory: { ...DEFAULT_FETCHING_STATE },
        purchaseTokens: { ...DEFAULT_FETCHING_STATE },
        exportNOM035: { ...DEFAULT_FETCHING_STATE },
    },
    error: { ...DEFAULT_NO_ERROR },
};

const reducer = (state = INITIAL_STATE, action) => {
    switch (action.type) {
        // FETCH DASHBOARD
        case Types.FETCH_DASHBOARD_START:
            return {
                ...state,
                fetching: {
                    ...state.fetching,
                    dashboard: { ...DEFAULT_FETCHING_STATE, state: true },
                },
            };
        case Types.FETCH_DASHBOARD_SUCCESS: {
            const d = action.payload || {};
            return {
                ...state,
                dashboard: {
                    tokenBalance: d.business?.tokenBalance || 0,
                    totalEmployees: d.employees?.total || 0,
                    activeEmployees: d.employees?.active || 0,
                    sessionsAssigned: d.sessions?.totalAssigned || 0,
                    sessionsUsed: d.sessions?.totalUsed || 0,
                    sessionsAvailable: d.sessions?.available || 0,
                    usageByMonth: d.usageByMonth || [],
                    businessName: d.business?.name || '',
                    planType: d.business?.planType || '',
                    recentTransactions: d.recentTransactions || [],
                },
                fetching: {
                    ...state.fetching,
                    dashboard: { ...DEFAULT_FETCHING_STATE },
                },
                error: { ...DEFAULT_NO_ERROR },
            };
        }
        case Types.FETCH_DASHBOARD_ERROR:
            return {
                ...state,
                fetching: {
                    ...state.fetching,
                    dashboard: { ...DEFAULT_FETCHING_STATE },
                },
                error: { timestamp: Date.now(), message: action.payload },
            };

        // FETCH EMPLOYEES
        case Types.FETCH_EMPLOYEES_START:
            return {
                ...state,
                fetching: {
                    ...state.fetching,
                    employees: { ...DEFAULT_FETCHING_STATE, state: true },
                },
            };
        case Types.FETCH_EMPLOYEES_SUCCESS:
            return {
                ...state,
                employees: {
                    data: action.payload.data || [],
                    total: action.payload.total || action.payload.data?.length || 0,
                },
                fetching: {
                    ...state.fetching,
                    employees: { ...DEFAULT_FETCHING_STATE },
                },
                error: { ...DEFAULT_NO_ERROR },
            };
        case Types.FETCH_EMPLOYEES_ERROR:
            return {
                ...state,
                fetching: {
                    ...state.fetching,
                    employees: { ...DEFAULT_FETCHING_STATE },
                },
                error: { timestamp: Date.now(), message: action.payload },
            };

        // ADD EMPLOYEE
        case Types.ADD_EMPLOYEE_START:
            return {
                ...state,
                fetching: {
                    ...state.fetching,
                    addEmployee: { ...DEFAULT_FETCHING_STATE, state: true },
                },
            };
        case Types.ADD_EMPLOYEE_SUCCESS:
            return {
                ...state,
                fetching: {
                    ...state.fetching,
                    addEmployee: { ...DEFAULT_FETCHING_STATE },
                },
                error: { ...DEFAULT_NO_ERROR },
            };
        case Types.ADD_EMPLOYEE_ERROR:
            return {
                ...state,
                fetching: {
                    ...state.fetching,
                    addEmployee: { ...DEFAULT_FETCHING_STATE },
                },
                error: { timestamp: Date.now(), message: action.payload },
            };

        // DELETE EMPLOYEE
        case Types.DELETE_EMPLOYEE_START:
            return {
                ...state,
                fetching: {
                    ...state.fetching,
                    deleteEmployee: { ...DEFAULT_FETCHING_STATE, state: true },
                },
            };
        case Types.DELETE_EMPLOYEE_SUCCESS:
            return {
                ...state,
                fetching: {
                    ...state.fetching,
                    deleteEmployee: { ...DEFAULT_FETCHING_STATE },
                },
                error: { ...DEFAULT_NO_ERROR },
            };
        case Types.DELETE_EMPLOYEE_ERROR:
            return {
                ...state,
                fetching: {
                    ...state.fetching,
                    deleteEmployee: { ...DEFAULT_FETCHING_STATE },
                },
                error: { timestamp: Date.now(), message: action.payload },
            };

        // ASSIGN SESSIONS
        case Types.ASSIGN_SESSIONS_START:
            return {
                ...state,
                fetching: {
                    ...state.fetching,
                    assignSessions: { ...DEFAULT_FETCHING_STATE, state: true },
                },
            };
        case Types.ASSIGN_SESSIONS_SUCCESS:
            return {
                ...state,
                fetching: {
                    ...state.fetching,
                    assignSessions: { ...DEFAULT_FETCHING_STATE },
                },
                error: { ...DEFAULT_NO_ERROR },
            };
        case Types.ASSIGN_SESSIONS_ERROR:
            return {
                ...state,
                fetching: {
                    ...state.fetching,
                    assignSessions: { ...DEFAULT_FETCHING_STATE },
                },
                error: { timestamp: Date.now(), message: action.payload },
            };

        // FETCH TOKEN HISTORY
        case Types.FETCH_TOKEN_HISTORY_START:
            return {
                ...state,
                fetching: {
                    ...state.fetching,
                    tokenHistory: { ...DEFAULT_FETCHING_STATE, state: true },
                },
            };
        case Types.FETCH_TOKEN_HISTORY_SUCCESS:
            return {
                ...state,
                tokenHistory: {
                    data: action.payload.transactions || [],
                    total: action.payload.transactions?.length || 0,
                },
                fetching: {
                    ...state.fetching,
                    tokenHistory: { ...DEFAULT_FETCHING_STATE },
                },
                error: { ...DEFAULT_NO_ERROR },
            };
        case Types.FETCH_TOKEN_HISTORY_ERROR:
            return {
                ...state,
                fetching: {
                    ...state.fetching,
                    tokenHistory: { ...DEFAULT_FETCHING_STATE },
                },
                error: { timestamp: Date.now(), message: action.payload },
            };

        // PURCHASE TOKENS
        case Types.PURCHASE_TOKENS_START:
            return {
                ...state,
                fetching: {
                    ...state.fetching,
                    purchaseTokens: { ...DEFAULT_FETCHING_STATE, state: true },
                },
            };
        case Types.PURCHASE_TOKENS_SUCCESS:
            return {
                ...state,
                fetching: {
                    ...state.fetching,
                    purchaseTokens: { ...DEFAULT_FETCHING_STATE },
                },
                error: { ...DEFAULT_NO_ERROR },
            };
        case Types.PURCHASE_TOKENS_ERROR:
            return {
                ...state,
                fetching: {
                    ...state.fetching,
                    purchaseTokens: { ...DEFAULT_FETCHING_STATE },
                },
                error: { timestamp: Date.now(), message: action.payload },
            };

        // FETCH WELLNESS SUMMARY
        case Types.FETCH_WELLNESS_SUMMARY_START:
            return {
                ...state,
                fetching: {
                    ...state.fetching,
                    wellnessSummary: { ...DEFAULT_FETCHING_STATE, state: true },
                },
            };
        case Types.FETCH_WELLNESS_SUMMARY_SUCCESS:
            return {
                ...state,
                wellnessSummary: action.payload,
                fetching: {
                    ...state.fetching,
                    wellnessSummary: { ...DEFAULT_FETCHING_STATE },
                },
                error: { ...DEFAULT_NO_ERROR },
            };
        case Types.FETCH_WELLNESS_SUMMARY_ERROR:
            return {
                ...state,
                fetching: {
                    ...state.fetching,
                    wellnessSummary: { ...DEFAULT_FETCHING_STATE },
                },
                error: { timestamp: Date.now(), message: action.payload },
            };

        // EXPORT NOM035
        case Types.EXPORT_NOM035_START:
            return {
                ...state,
                fetching: {
                    ...state.fetching,
                    exportNOM035: { ...DEFAULT_FETCHING_STATE, state: true },
                },
            };
        case Types.EXPORT_NOM035_SUCCESS:
            return {
                ...state,
                fetching: {
                    ...state.fetching,
                    exportNOM035: { ...DEFAULT_FETCHING_STATE },
                },
                error: { ...DEFAULT_NO_ERROR },
            };
        case Types.EXPORT_NOM035_ERROR:
            return {
                ...state,
                fetching: {
                    ...state.fetching,
                    exportNOM035: { ...DEFAULT_FETCHING_STATE },
                },
                error: { timestamp: Date.now(), message: action.payload },
            };

        // RESET ERROR
        case Types.RESET_ERROR:
            return {
                ...state,
                error: { ...DEFAULT_NO_ERROR },
            };

        default:
            return state;
    }
};

export default reducer;
